import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { COUNTRIES } from "../lib/countries";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
    AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Trash, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import ThemeToggle from "../components/layout/ThemeToggle";

export default function Settings() {
    const { user, updateMe, deleteAccount } = useAuth();
    const navigate = useNavigate();
    const [name, setName] = useState(user?.name || "");
    const [country, setCountry] = useState(user?.country || "");
    const [bio, setBio] = useState(user?.bio || "");
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await updateMe({ name, country, bio });
            toast.success("Settings saved");
        } catch { toast.error("Failed"); }
        finally { setSaving(false); }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await deleteAccount();
            toast.success("Account deleted");
            navigate("/auth", { replace: true });
        } catch {
            toast.error("Failed to delete account. Try again.");
            setDeleting(false);
        }
    };

    if (!user) return null;
    return (
        <div className="px-4 sm:px-6 lg:px-10 py-8 lg:py-12 max-w-2xl mx-auto" data-testid="settings-page">
            <h1 className="font-display font-black text-4xl tracking-tighter mb-2 brand-text">Settings</h1>
            <p className="text-muted-foreground mb-8">Personalise your Ideas Mine experience.</p>

            <form onSubmit={save} className="space-y-5 rounded-2xl border border-border bg-card p-6" data-testid="settings-form">
                <div>
                    <Label htmlFor="s-name">Display name</Label>
                    <Input id="s-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="settings-name" />
                </div>
                <div>
                    <Label>Country (your feed will switch instantly)</Label>
                    <Select value={country} onValueChange={setCountry}>
                        <SelectTrigger data-testid="settings-country"><SelectValue /></SelectTrigger>
                        <SelectContent className="max-h-72">
                            {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1.5">
                        Note: this changes your <b>feed</b> only. Subscription &amp; AI credit prices remain in your original sign-up region.
                    </p>
                </div>
                <div>
                    <Label htmlFor="s-bio">Bio</Label>
                    <Textarea id="s-bio" value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Tell the community about yourself…" data-testid="settings-bio" />
                </div>
                <Button type="submit" className="brand-button" disabled={saving} data-testid="settings-save">
                    {saving ? "Saving…" : "Save changes"}
                </Button>
            </form>

            <div className="mt-6 flex items-center justify-between rounded-2xl border border-border bg-card p-6">
                <div>
                    <div className="font-semibold">Appearance</div>
                    <div className="text-sm text-muted-foreground">Light, Dark, or System mode.</div>
                </div>
                <ThemeToggle />
            </div>

            {/* Danger zone — hidden for admin accounts */}
            {user.role !== "admin" && (
            <div className="mt-10" data-testid="danger-zone">
                <div className="flex items-center gap-2 mb-3">
                    <Warning size={16} weight="fill" className="text-red-500" />
                    <h2 className="text-xs font-black tracking-[0.25em] uppercase text-red-500">
                        Danger zone
                    </h2>
                </div>
                <div className="rounded-2xl border-2 border-red-500/30 bg-red-500/5 p-6 flex flex-wrap items-center gap-4">
                    <div className="flex-1 min-w-[16rem]">
                        <div className="font-semibold">Delete account</div>
                        <div className="text-sm text-muted-foreground mt-1 leading-relaxed">
                            Permanently delete your Ideas Mine account and all of your posts.
                            This action cannot be undone.
                        </div>
                    </div>
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <Button
                                variant="outline"
                                className="border-red-500/50 text-red-500 hover:bg-red-500/10 hover:text-red-500 shrink-0"
                                data-testid="delete-account-trigger"
                                disabled={deleting}
                            >
                                <Trash size={16} className="mr-1.5" />
                                {deleting ? "Deleting…" : "Delete account"}
                            </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent data-testid="delete-account-dialog">
                            <AlertDialogHeader>
                                <AlertDialogTitle>Delete your account?</AlertDialogTitle>
                                <AlertDialogDescription>
                                    This permanently deletes your account and all your posts.
                                    This action cannot be undone.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel data-testid="delete-account-cancel">
                                    Cancel
                                </AlertDialogCancel>
                                <AlertDialogAction
                                    data-testid="delete-account-confirm"
                                    onClick={handleDelete}
                                    className="bg-red-600 hover:bg-red-700 text-white"
                                >
                                    Yes, delete
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </div>
            </div>
            )}
        </div>
    );
}
